import IconButton from '@standardsdigital/ui-components/IconButton';
import LinkButton from '@standardsdigital/ui-components/LinkButton';
import DataList from 'components/global/DataList/DataList';
import DataListItem from 'components/global/DataListItem/DataListItem';
import type { DocumentLanguage } from 'types/general';
import { formatDate, localizePathname } from '../../../../../util/util';
import styles from './SubscriptionItem.module.css';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import type { SubscriptionModel } from 'redux/services/api.generated';
import {
  SubscriptionStatus,
  useAllowAutomaticAssignmentMutation,
  useReactivateSubscriptionMutation,
} from 'redux/services/api.generated';
import { useToast } from 'util/hooks';
import type { StatusIndicatorProps } from '../../../../global/StatusIndicator/StatusIndicator';
import StatusIndicator from '../../../../global/StatusIndicator/StatusIndicator';
import { downloadFile, getSubscriptionExportUrl } from 'util/fileUtil';
import { Link } from 'react-router-dom';
import EndSubscriptionModal from 'components/pages/Subscriptions/SubscriptionList/EndSubscriptionModal/EndSubscriptionModal';
import NoteModal from './NoteModal/NoteModal';
import Toggle from '@standardsdigital/ui-components/Toggle';
import { routes } from 'util/routes';

interface SubscriptionItemProps {
  readonly subscription: SubscriptionModel;
  readonly refetchSubscriptions: () => void;
  readonly isFetching: boolean;
  readonly index: number;
}

function SubscriptionItem({
  refetchSubscriptions,
  isFetching,
  subscription,
  index,
}: SubscriptionItemProps) {
  const {
    subscriptionId,
    description,
    serviceLevel,
    invoicePeriodEndDate,
    languages,
    subscriptionLineCount,
    userCount,
    subscriptionStatus,
    inactiveDate,
    contractDate,
    note,
    allowAutomaticAssignment,
    subscriptionPlanName,
  } = subscription;

  const { t } = useTranslation();
  const toast = useToast();
  const [discontinueModalOpen, setDiscontinueModalOpen] = useState(false);
  const [isDownloadingFile, setIsDownloadingFile] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [updateAllowAutomaticAssignment] = useAllowAutomaticAssignmentMutation();

  const [reactivateSubscription] = useReactivateSubscriptionMutation();

  const headingId = `${subscriptionId}-heading`;

  const documentLanguages = languages
    .map((lang) => t(`documentLanguages.${lang as DocumentLanguage}`))
    .join(', ');

  const isFutureDiscontinued =
    subscriptionStatus === SubscriptionStatus.Discontinued &&
    inactiveDate === invoicePeriodEndDate;

  const getStatusLabel = () => {
    const status = t(`subscriptionStatuses.${subscriptionStatus}`);
    if (subscriptionStatus === SubscriptionStatus.PendingActive) {
      return `${subscriptionStatus} ${contractDate ? formatDate(contractDate) : ''}`;
    }
    if (subscriptionStatus === SubscriptionStatus.Discontinued) {
      return t('subscriptions.discontinuedFrom', { date: formatDate(inactiveDate) });
    }
    if (inactiveDate) {
      return `${subscriptionStatus} ${formatDate(inactiveDate)}`;
    }
    return status;
  };

  const shouldShowReactivateButton =
    [SubscriptionStatus.Inactive, SubscriptionStatus.PendingInactive].includes(
      subscriptionStatus,
    ) || isFutureDiscontinued;

  const shouldShowEndSubscriptionButton =
    subscriptionStatus === SubscriptionStatus.Active;
  const shouldShowAllowAutomaticAssignment =
    subscriptionStatus === SubscriptionStatus.Active;

  const viewSubscriptionLinesText = () => {
    if (subscriptionLineCount === 0) {
      return t('subscriptions.noSubscriptionLines');
    }
    if (subscriptionLineCount === 1) {
      return t('subscriptions.viewSubscriptionLines.singular', {
        subscriptionLineCount,
      });
    }
    return t('subscriptions.viewSubscriptionLines.plural', {
      subscriptionLineCount,
    });
  };

  const handleUpdateAllowAutomaticAssignment = (checked: boolean) => {
    updateAllowAutomaticAssignment({
      subscriptionId,
      allowAutomaticAssignment: checked,
    })
      .unwrap()
      .then(() => {
        refetchSubscriptions();
      })
      .catch(() => {
        // Errors are handled in middleware.
      });
  };

  const handleReactivate = () => {
    reactivateSubscription({
      subscriptionId,
      isPendingInactive: subscriptionStatus === SubscriptionStatus.PendingInactive,
    })
      .unwrap()
      .then(() => {
        refetchSubscriptions();
        toast.success(
          t('subscriptions.subscriptionHasBeenReactivated', {
            subscriptionId,
          }),
        );
      })
      .catch(() => {
        // Errors are handled in middleware.
      });
  };

  const handleExport = async () => {
    setIsDownloadingFile(true);
    const result = await downloadFile(
      getSubscriptionExportUrl(subscriptionId),
      `${subscriptionId}.xlsx`,
    );

    if (!result) {
      toast.error(t('subscriptions.exportError'));
    }
    setIsDownloadingFile(false);
  };

  let statusColor: StatusIndicatorProps['color'] = 'neutral';
  switch (subscriptionStatus) {
    case SubscriptionStatus.Active:
      statusColor = 'success';
      break;
    case SubscriptionStatus.PendingInactive:
    case SubscriptionStatus.PendingActive:
    case SubscriptionStatus.Discontinued:
      statusColor = 'warning';
      break;
    case SubscriptionStatus.Inactive:
    case SubscriptionStatus.Expired:
      statusColor = 'danger';
      break;
    case SubscriptionStatus.Draft:
    case SubscriptionStatus.Quotation:
      break;
  }

  let subscriptionStyles = styles.subscription;
  if (discontinueModalOpen) {
    subscriptionStyles += ` ${styles.noFocus}`;
  }
  if (isFetching) {
    subscriptionStyles += ` ${styles.isFetching}`;
  }

  const disableSubscriptonLink = subscriptionLineCount === 0 || isFetching;

  return (
    <li
      key={subscriptionId}
      className={subscriptionStyles}
      tabIndex={index === 0 ? 0 : -1}
    >
      <EndSubscriptionModal
        isOpen={discontinueModalOpen}
        setIsOpen={setDiscontinueModalOpen}
        subscriptionId={subscriptionId}
        invoicePeriodEndDate={invoicePeriodEndDate}
        refetchSubscriptions={refetchSubscriptions}
      />
      <NoteModal
        isOpen={isNoteModalOpen}
        setIsOpen={setIsNoteModalOpen}
        note={note}
        subscriptionId={subscriptionId}
        refetchSubscriptions={refetchSubscriptions}
      />
      <article className={styles.cardInner} aria-labelledby={headingId}>
        <header>
          <h3 className="sd-heading-sm" id={headingId}>
            {subscriptionId} {description}
          </h3>
        </header>
        <StatusIndicator color={statusColor}>{getStatusLabel()}</StatusIndicator>
        <DataList className={styles.dataList}>
          {subscriptionPlanName ? (
            <DataListItem
              label={t('labels.subscriptionPlan')}
              value={subscriptionPlanName}
            />
          ) : null}
          {documentLanguages ? (
            <DataListItem label={t('labels.languages')} value={documentLanguages} />
          ) : null}
          {serviceLevel ? (
            <DataListItem label={t('labels.services')} value={serviceLevel} />
          ) : null}
          <DataListItem label={t('labels.users')} value={userCount} />
          <DataListItem
            label={t('labels.contractDate')}
            value={formatDate(contractDate)}
          />
          {note ? (
            <DataListItem label={t('note.internalNote')} value={note} fullWidth />
          ) : null}
        </DataList>

        {shouldShowAllowAutomaticAssignment ? (
          <Toggle
            checked={allowAutomaticAssignment}
            label={t('labels.allowAutoAssign')}
            disabled={isFetching}
            onCheckedChange={(checked) => {
              handleUpdateAllowAutomaticAssignment(checked);
            }}
          />
        ) : null}

        <footer className={styles.footer}>
          {shouldShowReactivateButton ? (
            <IconButton
              icon="Recycle"
              size="small"
              onClick={handleReactivate}
              disabled={isFetching}
            >
              {t('subscriptions.reactivate')}
            </IconButton>
          ) : null}
          {shouldShowEndSubscriptionButton ? (
            <IconButton
              icon="CalendarOff"
              size="small"
              onClick={() => setDiscontinueModalOpen(true)}
              disabled={isFetching}
            >
              {t('subscriptions.endSubscription')}
            </IconButton>
          ) : null}
          <IconButton
            size="small"
            icon="Download"
            disabled={isDownloadingFile || isFetching}
            onClick={() => handleExport()}
            className={styles.downloadButton}
            loading={isDownloadingFile}
          >
            {t('general.export')}
          </IconButton>
          <IconButton
            onClick={() => setIsNoteModalOpen(true)}
            icon="Edit"
            disabled={isFetching}
          >
            {note ? t('note.editNote') : t('note.createNote')}
          </IconButton>
          <LinkButton
            variant="primary"
            className={styles.viewButton}
            icon={subscriptionLineCount > 0 ? 'Eye' : undefined}
            component={disableSubscriptonLink ? undefined : Link}
            to={localizePathname(
              routes.subscriptionLinesDetails.to({ subscriptionId }),
            )}
          >
            {viewSubscriptionLinesText()}
          </LinkButton>
        </footer>
      </article>
    </li>
  );
}

export default SubscriptionItem;
